import { mergeDeep, cloneDeep } from 'apollo-utilities';
import { useCallback, useMemo } from 'react';
import { ClipFragment } from '../../../fragments.generated';
import { ClipsRefs, StudioClipsDataAndActions } from './common';
import { useApolloCacheUtilities } from '../../../../util/apolloUtils';
import useClipCacheUtilities from './useClipCacheUtilities';

const usePendingClips = (projectId: string) => {
  const { client, identityInCache } = useApolloCacheUtilities();
  const { writeClipFragmentRef, readClipFragment } = useClipCacheUtilities();

  const addPendingClip = useCallback<
    StudioClipsDataAndActions[1]['addPendingClip']
  >(
    clip => {
      const defaults = {
        __typename: 'Clip',
        fileName: '',
        src: '',
        isDeleted: false,
        isCreditableBy: null,
        audioFormat: 'mp3',
        combinedFrom: null,
        streamRequest: null,
        ttsVersion: 'v9',
        commentCount: 0,
        unviewedCommentCount: 0,
      };
      const clipToBeWritten = mergeDeep(defaults, clip);
      const pendingClipRef = writeClipFragmentRef(clipToBeWritten);
      if (!pendingClipRef) {
        return;
      }
      client.cache.modify({
        id: identityInCache('Project', projectId),
        fields: {
          clips_V2: (existingClipsRefs: any): ClipsRefs => {
            const newEdges = cloneDeep(existingClipsRefs.edges);
            newEdges.unshift({
              node: pendingClipRef,
              highlights: null,
              // NOTE: assigning a temp cursor value so this edge is properly resolved by the
              // relayStylePagination merge function.
              cursor: `__tempcursor:${clip.id}`,
            });
            const clipWithNewEdgeResponse = mergeDeep<Array<ClipsRefs>>(
              existingClipsRefs,
              {
                edges: newEdges,
              }
            );
            return clipWithNewEdgeResponse;
          },
        },
      });
    },
    [writeClipFragmentRef, client.cache, identityInCache, projectId]
  );

  /**
   *  @description this method takes the incomingClipId - "client generated" or
   * "server generated" and a partial or full clip object - the reference is either updated
   * or if the clip is being marked as completed - we are adding the new clip reference
   * to the list of edges
   *
   * Noting that we only have a temporary id in a combine clips context and
   * not within a typical clip creation context - this usage change occured during
   * clip cancellation
   */
  const updatePendingClip = useCallback<
    StudioClipsDataAndActions[1]['updatePendingClip']
  >(
    (incomingClipId, incomingClipProps) => {
      const incomingClipRef = identityInCache('Clip', incomingClipId);
      const clipInCache = readClipFragment(incomingClipId);
      if (!incomingClipRef || !clipInCache) {
        // eslint-disable-next-line no-console
        console.warn('Unable to update pending clip, no longer exists');
        return;
      }
      const data = mergeDeep<[ClipFragment, Partial<ClipFragment>]>(
        clipInCache,
        incomingClipProps
      );
      // Note that `serverId` is always `incomingClipId` in non-combine context
      const serverId = incomingClipProps.id;
      const newClipRef = writeClipFragmentRef(data, {
        id: serverId,
      });
      if (!newClipRef) {
        // eslint-disable-next-line no-console
        console.warn('Failed to update pending clip');
        return;
      }
      // only write the clip to the edge list if the incoming clip
      // reference uses a server id
      const clipIdIsChanging = serverId && incomingClipId !== serverId;
      if (!clipIdIsChanging) {
        return;
      }
      client.cache.modify({
        id: identityInCache('Project', projectId),
        fields: {
          clips_V2: (existingClipsRefs, { toReference }) => {
            const oldEdges = cloneDeep(existingClipsRefs.edges);
            const refToReplace = toReference(incomingClipRef);
            // replace the temp edge with the new server edge reference
            const edges = oldEdges.map((edge: any) => {
              const currRef = toReference(edge.node.__ref);
              const isEdgeToReplace = currRef?.__ref === refToReplace?.__ref;
              /**
               * Note we may potentially want to compute highlights based on
               * search, but we currently are setting them as empty
               */
              return isEdgeToReplace
                ? { ...edge, node: newClipRef, highlights: null }
                : edge;
            });
            const clipsWithNewEdgeResponse = mergeDeep<Array<ClipsRefs>>(
              existingClipsRefs,
              {
                edges,
              }
            );
            return clipsWithNewEdgeResponse;
          },
        },
      });
      client.cache.evict({ id: incomingClipRef });
      client.cache.gc();
    },
    [
      identityInCache,
      client.cache,
      readClipFragment,
      writeClipFragmentRef,
      projectId,
    ]
  );

  const deletePendingClip = useCallback<
    StudioClipsDataAndActions[1]['deletePendingClip']
  >(
    pendingClipId => {
      client.cache.evict({ id: identityInCache('Clip', pendingClipId) });
      client.cache.gc();
    },
    [client.cache, identityInCache]
  );

  return useMemo(
    () => ({
      addPendingClip,
      updatePendingClip,
      deletePendingClip,
    }),
    [addPendingClip, updatePendingClip, deletePendingClip]
  );
};

export default usePendingClips;
